/**
 * DENOVO SHOES - CROSS-SELL ESTILO ADIDAS
 * Versão 12 Final - Com Logs Detalhados
 */

class CartUpsellsSmart {
  constructor() {
    this.container = document.querySelector('[data-cart-upsells]');
    if (!this.container) return;

    this.toggleButton = this.container.querySelector('[data-upsell-toggle]');
    this.content = this.container.querySelector('[data-upsell-content]');
    this.icon = this.container.querySelector('[data-upsell-icon]');
    this.productsWrapper = this.container.querySelector('[data-upsell-products]');
    
    this.mapUrl = '/assets/cross-sell-map.json';
    this.maxRecommendations = 6;
    this.swiper = null;
    
    this.WEIGHTS = {
      curated: 5,
      genderMatch: 4,
      recommendationApi: 3,
      tagMatch: 2,
      priceProximity: 1,
      bestseller: 1
    };

    this.init();
  }

  async init() {
    try {
      console.log('🚀 Inicializando CartUpsellsSmart...');
      
      const fallbackMap = {
        "bestsellers": {
          "masculino": [
            "tenis-masculino-kyoto-preto-denovo",
            "tenis-masculino-rio-preto-denovo",
            "tenis-masculino-paris-bege-denovo"
          ],
          "feminino": [
            "tenis-feminino-kyoto-preto-denovo",
            "tenis-feminino-kyoto-cinza-denovo",
            "tenis-feminino-rio-preto-denovo"
          ]
        },
        "by_product": {},
        "by_tag": {},
        "defaults": {
          "geral": [
            "tenis-masculino-kyoto-preto-denovo",
            "tenis-feminino-kyoto-preto-denovo",
            "tenis-masculino-rio-preto-denovo",
            "tenis-feminino-kyoto-cinza-denovo",
            "tenis-masculino-paris-bege-denovo",
            "tenis-feminino-rio-preto-denovo"
          ]
        }
      };
      
      const [cart, curatedMap] = await Promise.all([
        this.getCart(),
        this.fetchJson(this.mapUrl).catch((e) => {
          console.warn('⚠️ JSON externo não carregou, usando fallback');
          return fallbackMap;
        })
      ]);
      
      console.log('📦 Carrinho:', cart);
      console.log('📋 Mapa:', curatedMap);

      const recommendations = await this.buildSmartRecommendations(cart, curatedMap);
      
      console.log('✅ Recomendações finais:', recommendations.length, 'produtos');
      
      this.renderProducts(recommendations);
      this.initSwiper();
      this.bindEvents();
    } catch (error) {
      console.error('❌ Erro ao inicializar:', error);
      this.showError();
    }
  }

  async getCart() {
    return this.fetchJson('/cart.js');
  }

  async fetchJson(url) {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Fetch error: ${res.status}`);
    return res.json();
  }

  async buildSmartRecommendations(cart, curatedMap) {
    // Se carrinho vazio, retorna bestsellers gerais
    if (!cart.items || cart.items.length === 0) {
      console.log('📦 Carrinho vazio, usando fallback');
      return this.getFallbackRecommendations(curatedMap);
    }

    const genderInfo = await this.detectCartGender(cart);
    console.log('🎯 Gênero detectado:', genderInfo);

    // Se não detectou gênero, usa bestsellers gerais
    if (!genderInfo.detected) {
      console.log('⚠️ Gênero não detectado, usando bestsellers gerais');
      return this.getBestsellers(curatedMap, 'geral');
    }

    // Monta pool de candidatos
    const candidates = await this.buildCandidatePool(cart, curatedMap, genderInfo);
    console.log('📊 Candidatos encontrados:', candidates.length);

    // Se não achou candidatos, vai direto para bestsellers
    if (candidates.length === 0) {
      console.log('⚠️ Nenhum candidato, usando bestsellers');
      return this.getBestsellers(curatedMap, genderInfo.predominant);
    }

    // Filtra por gênero e busca detalhes
    const filtered = await this.filterByGender(candidates, genderInfo);
    console.log('✅ Após filtro de gênero:', filtered.length);
    
    const sorted = filtered.sort((a, b) => b.score - a.score);
    const topN = sorted.slice(0, this.maxRecommendations);
    
    let products = await this.fetchProductDetails(topN);
    console.log('📦 Produtos carregados:', products.length);

    // Completa com bestsellers se necessário
    if (products.length < this.maxRecommendations) {
      console.log('📈 Completando com bestsellers...');
      const bestsellers = await this.getBestsellers(curatedMap, genderInfo.predominant);
      const inResultIds = new Set(products.map(p => String(p.id)));
      
      for (const bs of bestsellers) {
        if (products.length >= this.maxRecommendations) break;
        if (!inResultIds.has(String(bs.id))) {
          products.push(bs);
          console.log('➕ Adicionado bestseller:', bs.handle);
        }
      }
    }

    // Garante que sempre retorna algo
    if (products.length === 0) {
      console.warn('⚠️ Nenhum produto após todas tentativas, forçando bestsellers');
      return this.getBestsellers(curatedMap, 'geral');
    }

    return products;
  }

  async detectCartGender(cart) {
    const genderCounts = { masculino: 0, feminino: 0 };

    for (const item of cart.items) {
      try {
        const handle = item.handle || (item.url && item.url.split('/products/')[1]?.split('?')[0]);
        if (!handle) continue;
        
        const product = await this.fetchJson(`/products/${handle}.js`).catch(() => null);
        
        if (product && Array.isArray(product.tags)) {
          const tags = product.tags.map(t => t.toLowerCase().trim());
          
          if (tags.includes('masculino')) {
            genderCounts.masculino++;
          }
          
          if (tags.includes('feminino')) {
            genderCounts.feminino++;
          }
        }
      } catch (e) {
        console.warn('Erro ao buscar produto:', e);
      }
    }

    const total = genderCounts.masculino + genderCounts.feminino;
    
    if (total === 0) {
      return { detected: false, predominant: null, ratio: 0, counts: genderCounts };
    }

    const ratio = Math.max(genderCounts.masculino, genderCounts.feminino) / total;
    if (ratio < 0.7) {
      return {
        detected: true,
        predominant: 'misto',
        ratio: ratio,
        counts: genderCounts
      };
    }

    const predominant = genderCounts.masculino > genderCounts.feminino ? 'masculino' : 'feminino';
    return {
      detected: true,
      predominant: predominant,
      ratio: ratio,
      counts: genderCounts
    };
  }

  async buildCandidatePool(cart, curatedMap, genderInfo) {
    const inCartIds = new Set(cart.items.map(i => String(i.product_id)));
    const candidateScores = {};

    const ensureCandidate = (id) => {
      const key = String(id);
      if (!candidateScores[key]) {
        candidateScores[key] = {
          id: id,
          score: 0,
          reasons: {},
          handle: null,
          tags: []
        };
      }
      return candidateScores[key];
    };

    // 1. Curadoria manual
    if (curatedMap && curatedMap.by_product) {
      for (const item of cart.items) {
        const pid = String(item.product_id);
        if (curatedMap.by_product[pid]) {
          curatedMap.by_product[pid].forEach(recId => {
            if (inCartIds.has(String(recId))) return;
            const c = ensureCandidate(recId);
            c.score += this.WEIGHTS.curated;
            c.reasons['curated'] = (c.reasons['curated'] || 0) + 1;
          });
        }
      }
    }

    // 2. API de Recomendações
    for (const item of cart.items) {
      try {
        const recommendations = await this.getShopifyRecommendations(item.product_id);
        for (const rec of recommendations) {
          if (inCartIds.has(String(rec.id))) continue;
          const c = ensureCandidate(rec.id);
          c.score += this.WEIGHTS.recommendationApi;
          c.reasons['recommendationApi'] = (c.reasons['recommendationApi'] || 0) + 1;
          c.handle = rec.handle;
        }
      } catch (e) {
        console.warn('API recommendations falhou:', e);
      }
    }

    // 3. Tag matching
    if (curatedMap && curatedMap.by_tag) {
      for (const item of cart.items) {
        try {
          const handle = item.handle || (item.url && item.url.split('/products/')[1]?.split('?')[0]);
          if (!handle) continue;
          
          const product = await this.fetchJson(`/products/${handle}.js`).catch(() => null);
          if (!product) continue;

          const tags = Array.isArray(product.tags)
            ? product.tags.map(t => t.toLowerCase().trim())
            : [];
          
          tags.forEach(tag => {
            const mapped = curatedMap.by_tag[tag];
            if (!mapped) return;
            
            mapped.forEach(recId => {
              if (inCartIds.has(String(recId))) return;
              
              const c = ensureCandidate(recId);
              c.score += this.WEIGHTS.tagMatch;
              c.reasons['tagMatch'] = (c.reasons['tagMatch'] || 0) + 1;
            });
          });
        } catch (e) {
          console.warn('Erro tag matching:', e);
        }
      }
    }

    return Object.values(candidateScores);
  }

  async getShopifyRecommendations(productId) {
    try {
      const res = await fetch(`/recommendations/products?product_id=${productId}&limit=10`);
      if (!res.ok) return [];
      
      const html = await res.text();
      
      try {
        const json = JSON.parse(html);
        return json.products || [];
      } catch (e) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const links = Array.from(doc.querySelectorAll('a[href*="/products/"]'));
        
        return links.map(a => {
          const match = a.getAttribute('href').match(/\/products\/([^\/\?]+)/);
          return match ? { handle: match[1], id: null } : null;
        }).filter(Boolean);
      }
    } catch (e) {
      return [];
    }
  }

  async filterByGender(candidates, genderInfo) {
    const filtered = [];

    for (const candidate of candidates) {
      try {
        let handle = candidate.handle;
        
        // Se não tem handle, tenta buscar pelo ID
        if (!handle && candidate.id) {
          console.log('⚠️ Candidato sem handle, pulando:', candidate.id);
          continue;
        }

        // Busca dados do produto
        const product = await this.fetchJson(`/products/${handle}.js`).catch(() => null);
        if (!product) {
          console.warn('❌ Produto não encontrado:', handle);
          continue;
        }

        const tags = Array.isArray(product.tags)
          ? product.tags.map(t => t.toLowerCase().trim())
          : [];
        
        candidate.tags = tags;
        candidate.product = product;

        // Se carrinho é misto, aceita tudo
        if (genderInfo.predominant === 'misto') {
          filtered.push(candidate);
          console.log('✅ Aceito (misto):', handle);
          continue;
        }

        // Se carrinho é masculino
        if (genderInfo.predominant === 'masculino') {
          if (tags.includes('masculino')) {
            candidate.score += this.WEIGHTS.genderMatch;
            filtered.push(candidate);
            console.log('✅ Aceito (masculino):', handle);
          } else {
            console.log('❌ Rejeitado (não masculino):', handle);
          }
          continue;
        }

        // Se carrinho é feminino
        if (genderInfo.predominant === 'feminino') {
          if (tags.includes('feminino')) {
            candidate.score += this.WEIGHTS.genderMatch;
            filtered.push(candidate);
            console.log('✅ Aceito (feminino):', handle);
          } else {
            console.log('❌ Rejeitado (não feminino):', handle);
          }
          continue;
        }
        
      } catch (e) {
        console.warn('❌ Erro ao filtrar candidato:', e);
      }
    }

    return filtered;
  }

  async fetchProductDetails(candidates) {
    const products = [];

    for (const candidate of candidates) {
      if (candidate.product) {
        products.push(candidate.product);
      } else if (candidate.handle) {
        try {
          const product = await this.fetchJson(`/products/${candidate.handle}.js`);
          if (product) products.push(product);
        } catch (e) {}
      }
    }

    return products;
  }

  async getBestsellers(curatedMap, gender) {
    console.log('🏆 Buscando bestsellers para:', gender);
    
    if (!curatedMap || !curatedMap.bestsellers) {
      console.warn('⚠️ Mapa de bestsellers não encontrado');
      return [];
    }

    let handles = [];

    if (gender === 'misto') {
      const masc = curatedMap.bestsellers.masculino || [];
      const fem = curatedMap.bestsellers.feminino || [];
      const max = Math.max(masc.length, fem.length);
      
      for (let i = 0; i < max; i++) {
        if (masc[i]) handles.push(masc[i]);
        if (fem[i]) handles.push(fem[i]);
      }
    } else if (gender === 'masculino' || gender === 'feminino') {
      handles = curatedMap.bestsellers[gender] || [];
    } else {
      // Fallback para "geral" ou qualquer outro valor
      handles = curatedMap.defaults?.geral || [];
      
      // Se defaults.geral não existir, pega masculino + feminino
      if (handles.length === 0) {
        handles = [
          ...(curatedMap.bestsellers.masculino || []),
          ...(curatedMap.bestsellers.feminino || [])
        ];
      }
    }

    console.log('📋 Handles a buscar:', handles);

    const products = [];
    for (const handle of handles) {
      try {
        const product = await this.fetchJson(`/products/${handle}.js`);
        if (product) {
          products.push(product);
          console.log('✅ Bestseller carregado:', handle);
        }
      } catch (e) {
        console.warn(`❌ Handle não encontrado: ${handle}`);
      }
    }

    console.log('🏆 Total bestsellers carregados:', products.length);
    return products;
  }

  async getFallbackRecommendations(curatedMap) {
    return this.getBestsellers(curatedMap, 'geral');
  }

  renderProducts(products) {
    console.log('🎨 Renderizando produtos:', products.length);
    
    if (!products || products.length === 0) {
      console.warn('⚠️ Nenhum produto para renderizar');
      this.showEmpty();
      return;
    }

    this.productsWrapper.innerHTML = '';

    products.forEach((product, index) => {
      console.log(`➕ Renderizando produto ${index + 1}:`, product.handle);
      const slide = this.createProductCard(product);
      this.productsWrapper.appendChild(slide);
    });
    
    console.log('✅ Renderização completa!');
  }

  createProductCard(product) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const img = product.featured_image?.src || product.images?.[0] || '';
    const title = product.title || 'Produto';
    const variant = product.variants?.[0];
    const price = variant?.price ? this.formatMoney(variant.price) : '';
    const comparePrice = variant?.compare_at_price > variant?.price 
      ? this.formatMoney(variant.compare_at_price) 
      : null;

    const category = this.extractCategory(product.tags);

    slide.innerHTML = `
      <div class="denovo-product-card">
        <a href="${product.url || `/products/${product.handle}`}" class="denovo-product-card__image-wrapper">
          <img
            src="${img}"
            alt="${this.escapeHtml(title)}"
            class="denovo-product-card__image"
            loading="lazy">
        </a>
        
        <div class="denovo-product-card__content">
          <h4 class="denovo-product-card__title">
            <a href="${product.url || `/products/${product.handle}`}">${this.escapeHtml(title)}</a>
          </h4>
          
          <p class="denovo-product-card__category">${category}</p>
          
          <div class="denovo-product-card__price">
            ${price}
            ${comparePrice ? `<span class="denovo-product-card__price--compare">${comparePrice}</span>` : ''}
          </div>
          
          ${this.createSizeSelector(product)}
          
          <button
            class="denovo-btn-add"
            data-add-to-cart
            data-product-id="${product.id}"
            data-variant-id="${variant.id}">
            Adicionar
          </button>
        </div>
      </div>
    `;

    return slide;
  }

  createSizeSelector(product) {
    const sizes = product.variants
      .filter(v => v.title && v.title.match(/^\d+$/))
      .map(v => ({
        id: v.id,
        size: v.title,
        available: v.available
      }));
  
    if (sizes.length === 0) return '';
    
    return `
      <div class="denovo-sizes">
        ${sizes.map((v, index) => `
          <button
            class="denovo-size ${index === 0 ? 'selected' : ''}"
            data-variant-id="${v.id}"
            data-size="${v.size}"
            ${!v.available ? 'disabled' : ''}
            onclick="
              if(!this.disabled) {
                this.closest('.swiper-slide').querySelectorAll('.denovo-size').forEach(b => {
                  b.classList.remove('selected');
                });
                this.classList.add('selected');
                const btn = this.closest('.swiper-slide').querySelector('[data-add-to-cart]');
                if(btn) btn.dataset.variantId = '${v.id}';
              }
            ">
            ${v.size}
          </button>
        `).join('')}
      </div>
    `;
  }

  extractCategory(tags) {
    if (!tags || !Array.isArray(tags)) return 'Tênis';
    const tagArray = tags.map(t => t.toLowerCase().trim());
    
    if (tagArray.includes('sportswear') || tagArray.includes('esportivo')) return 'Sportswear';
    if (tagArray.includes('casual')) return 'Casual';
    if (tagArray.includes('formal')) return 'Formal';
    if (tagArray.includes('originals') || tagArray.includes('streetwear')) return 'Originals';
    
    return 'Tênis';
  }

  initSwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }

    this.swiper = new Swiper('.denovo-swiper', {
      slidesPerView: 1,
      spaceBetween: 16,
      
      pagination: {
        el: '.denovo-pagination',
        clickable: true,
        dynamicBullets: true,
      },
      
      navigation: {
        nextEl: '.denovo-arrow--next',
        prevEl: '.denovo-arrow--prev',
      },
      
      speed: 400,
      
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 16,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
      },
      
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
    });
  }

  bindEvents() {
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => this.toggleContent());
    }

    const addButtons = this.container.querySelectorAll('[data-add-to-cart]');
    addButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  toggleContent() {
    const isExpanded = this.toggleButton.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.content.style.maxHeight = '0px';
      this.content.style.opacity = '0';
      this.toggleButton.setAttribute('aria-expanded', 'false');
    } else {
      this.content.style.maxHeight = this.content.scrollHeight + 'px';
      this.content.style.opacity = '1';
      this.toggleButton.setAttribute('aria-expanded', 'true');
    }
  }

  async handleAddToCart(e) {
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    
    if (!variantId) {
      console.error('Variant ID não encontrado');
      return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'ADICIONANDO...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error('Erro ao adicionar produto');
      }

      const data = await response.json();
      
      button.textContent = 'ADICIONADO ✓';
      
      // 🔥 Busca carrinho completo atualizado
      const updatedCart = await this.getCart();
      console.log('🛒 Carrinho atualizado:', updatedCart);
      
      // Atualiza UI com dados completos
      this.updateCartUI(updatedCart);
      
      // Dispara eventos para compatibilidade com o tema
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: updatedCart }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Erro ao adicionar ao carrinho:', error);
      button.textContent = 'ERRO';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  /**
   * Atualiza a UI do carrinho sem reload
   */
  async updateCartUI(cartData) {
    try {
      console.log('🔄 Atualizando UI do carrinho...');
      console.log('💰 Total do carrinho:', cartData.total_price, 'centavos');
      console.log('📦 Quantidade de itens:', cartData.item_count);
      
      // 1. Atualiza contador do carrinho (header)
      this.updateCartCount(cartData.item_count);
      
      // 2. Atualiza listagem de produtos no carrinho (cart page)
      await this.updateCartItems();
      
      // 3. Atualiza subtotal
      this.updateCartTotal(cartData.total_price);
      
      // 4. Atualiza APENAS os atributos da barra de frete (não o conteúdo)
      this.updateShippingBarAttributes(cartData.total_price);
      
      console.log('✅ UI do carrinho atualizada com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao atualizar UI:', error);
      console.warn('⚠️ Fazendo reload como fallback...');
      // Fallback: recarrega a página se falhar
      window.location.reload();
    }
  }

  /**
   * Atualiza contador no header
   */
  updateCartCount(count) {
    const counters = document.querySelectorAll('[data-cart-count], .cart-count, .cart__count');
    counters.forEach(counter => {
      counter.textContent = count;
      counter.setAttribute('data-cart-count', count);
    });
  }

  /**
   * Atualiza listagem de produtos do carrinho
   * PRESERVA a barra de frete grátis
   */
  async updateCartItems() {
    try {
      const response = await fetch('/cart?view=ajax');
      if (!response.ok) return;
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Atualiza produtos do carrinho (Impulse)
      const cartItemsOld = document.querySelector('[data-products], .cart__items, .cart-items');
      const cartItemsNew = doc.querySelector('[data-products], .cart__items, .cart-items');
      
      if (cartItemsOld && cartItemsNew) {
        cartItemsOld.innerHTML = cartItemsNew.innerHTML;
        console.log('✅ Produtos do carrinho atualizados');
      }
      
      // Atualiza cart drawer se existir
      const drawerOld = document.querySelector('[data-cart-ajax], .cart-drawer__items');
      const drawerNew = doc.querySelector('[data-cart-ajax], .cart-drawer__items');
      
      if (drawerOld && drawerNew) {
        drawerOld.innerHTML = drawerNew.innerHTML;
        console.log('✅ Cart drawer atualizado');
      }
      
      // 🔥 IMPORTANTE: NÃO toca na barra de frete - ela fica como está
      
    } catch (error) {
      console.warn('Erro ao atualizar items:', error);
    }
  }

  /**
   * Atualiza subtotal
   * IMPORTANTE: Exclui a shipping-bar para não sobrescrever
   */
  updateCartTotal(totalPrice) {
    console.log('💵 Atualizando subtotal:', totalPrice);
    
    if (!totalPrice || isNaN(totalPrice)) {
      console.warn('⚠️ Total inválido recebido:', totalPrice);
      return;
    }
    
    const formattedPrice = this.formatMoney(totalPrice);
    console.log('💵 Preço formatado:', formattedPrice);
    
    // Busca elementos de subtotal, mas EXCLUI a shipping-bar
    const totals = document.querySelectorAll('[data-subtotal], .cart__subtotal-price, .cart-total');
    
    // Filtra para remover qualquer elemento que seja ou esteja dentro de shipping-bar
    const validTotals = Array.from(totals).filter(el => {
      // Exclui se for a própria shipping-bar
      if (el.tagName === 'SHIPPING-BAR' || el.classList.contains('shipping-bar')) {
        console.log('💵 ⚠️ Ignorando shipping-bar:', el.tagName);
        return false;
      }
      
      // Exclui se estiver dentro de shipping-bar
      if (el.closest('shipping-bar') || el.closest('.shipping-bar')) {
        console.log('💵 ⚠️ Ignorando elemento dentro de shipping-bar');
        return false;
      }
      
      return true;
    });
    
    console.log('💵 Elementos de total encontrados:', validTotals.length);
    
    validTotals.forEach((total, index) => {
      total.textContent = formattedPrice;
      console.log(`💵 Total ${index + 1} atualizado:`, total.className || total.tagName);
    });
  }

  /**
   * Atualiza apenas os ATRIBUTOS da barra de frete (não o conteúdo de texto)
   * Isso faz a barra reagir visualmente sem sobrescrever o texto interno
   */
  updateShippingBarAttributes(totalPrice) {
    console.log('🚚 Atualizando atributos da barra de frete...');
    
    const shippingBar = document.querySelector('shipping-bar') || 
                        document.querySelector('[data-shipping-bar]') ||
                        document.querySelector('.shipping-bar');
    
    if (!shippingBar) {
      console.log('🚚 Barra de frete não encontrada');
      return;
    }
    
    if (!totalPrice || isNaN(totalPrice)) {
      console.warn('⚠️ Total inválido para frete:', totalPrice);
      return;
    }
    
    console.log('🚚 Barra encontrada:', shippingBar.tagName);
    console.log('🚚 Total atual:', totalPrice, 'centavos');
    
    // Pega o threshold da própria barra (padrão 30000 = R$ 300)
    const threshold = parseInt(shippingBar.getAttribute('data-shipping-threshold')) || 30000;
    console.log('🚚 Threshold:', threshold, 'centavos');
    
    // Calcula progresso
    const percentage = Math.min(100, Math.max(0, (totalPrice / threshold) * 100));
    console.log('🚚 Progresso:', percentage.toFixed(2) + '%');
    
    // Atualiza os atributos (isso faz o custom element reagir)
    shippingBar.setAttribute('data-cart-total', totalPrice);
    shippingBar.setAttribute('data-progress', `calc(${percentage.toFixed(2)}% + 2px)`);
    
    // Se atingiu o frete grátis, adiciona classe de sucesso
    if (totalPrice >= threshold) {
      shippingBar.classList.add('shipping-bar--complete');
      console.log('🚚 ✅ Frete grátis desbloqueado!');
    } else {
      shippingBar.classList.remove('shipping-bar--complete');
      console.log('🚚 📊 Progresso atualizado');
    }
    
    // Dispara evento customizado para o custom element reagir
    shippingBar.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: { total: totalPrice } 
    }));
    
    console.log('🚚 ✅ Atributos atualizados com sucesso!');
  }

  showEmpty() {
    this.productsWrapper.innerHTML = `
      <div class="swiper-slide">
        <div class="denovo-cross-sell__loading">
          <p>Nenhuma sugestão disponível no momento.</p>
        </div>
      </div>
    `;
  }

  showError() {
    if (this.productsWrapper) {
      this.productsWrapper.innerHTML = `
        <div class="swiper-slide">
          <div class="denovo-cross-sell__loading">
            <p>Erro ao carregar sugestões.</p>
          </div>
        </div>
      `;
    }
  }

  formatMoney(cents) {
    const value = Number(cents) / 100;
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartUpsellsSmart();
  });
} else {
  new CartUpsellsSmart();
}

document.addEventListener('cart:refresh', () => {
  new CartUpsellsSmart();
});

document.addEventListener('cart:updated', () => {
  setTimeout(() => {
    new CartUpsellsSmart();
  }, 500);
});